import { api } from "./client";

// Admin User Impersonation — the staff-side client for the admin
// impersonation + QA-user provisioning endpoints. Every call here is
// ADMIN-only and permission-gated server-side (`Users.Impersonate`); the
// client-side gates in LogInAsAction / EnsureQaUserModal only decide whether
// to OFFER the control.
//
// The portal exit endpoint deliberately lives in `portal/api/impersonation.ts`
// instead: it rides the PORTAL cookie, so it must go through the portal HTTP
// client, and portal isolation forbids the portal bundle importing this file.

/** `kind` tells the caller which surface the new session lands on. */
export type ImpersonationKind = "staff" | "portal";

export interface ImpersonationStartResultDto {
  kind: ImpersonationKind;
  /** Human label for the impersonated account (name / email). */
  targetLabel: string;
  /** Instant the impersonated session hard-stops. */
  expiresAt: string;
  /** MaterialAudience name for a portal target; null for a staff target. */
  portalAccountType: string | null;
}

/**
 * Result of `POST /api/admin/qa-users/ensure`. `created` distinguishes a
 * freshly-minted QA account from one that already existed, so the UI can say
 * which happened rather than implying it always creates.
 */
export interface EnsureQaUserResultDto {
  kind: ImpersonationKind;
  id: string;
  displayName: string;
  email: string;
  /** Set for a staff QA user; null for a portal one. */
  roleCode: string | null;
  /** Set for a portal QA user (a MaterialAudience name); null for staff. */
  accountType: string | null;
  created: boolean;
}

/** Exactly one of `roleCode` / `accountType` — the server rejects both/neither. */
export interface EnsureQaUserRequest {
  roleCode?: string;
  accountType?: string;
}

/**
 * Machine-readable code the server puts on the `ApiResponse.details` envelope
 * when it REFUSES a second concurrent portal impersonation (HTTP 400).
 *
 * Callers branch on this and never on the prose message. The message is copy —
 * it will be reworded, and a client that string-matched it would silently stop
 * recognising the refusal the first time someone did. The code is the contract.
 */
export const PORTAL_IMPERSONATION_ACTIVE_CODE = "portalImpersonationActive";

/** Shape of that `details` payload. */
export interface PortalImpersonationActiveDetails {
  code: typeof PORTAL_IMPERSONATION_ACTIVE_CODE;
  /** Display label of the portal session that is ALREADY live. */
  targetLabel: string;
}

/**
 * Narrows `ApiError.details`, which is typed `unknown` precisely because the
 * envelope carries whatever the endpoint put there. A cast would assert a shape
 * nobody checked; this checks both fields, so a payload missing either falls
 * through to the ordinary error path rather than rendering `undefined`.
 */
export function isPortalImpersonationActiveDetails(
  details: unknown,
): details is PortalImpersonationActiveDetails {
  if (typeof details !== "object" || details === null) return false;
  const candidate = details as { code?: unknown; targetLabel?: unknown };
  return (
    candidate.code === PORTAL_IMPERSONATION_ACTIVE_CODE &&
    typeof candidate.targetLabel === "string"
  );
}

export const impersonationApi = {
  startStaff: (targetUserId: string) =>
    api.post<ImpersonationStartResultDto>(
      `/admin/impersonation/staff/${targetUserId}/start`,
    ),
  startPortal: (portalUserId: string) =>
    api.post<ImpersonationStartResultDto>(
      `/admin/impersonation/portal/${portalUserId}/start`,
    ),
  /** Ends a STAFF impersonation (staff cookie). The portal exit is a separate
   *  endpoint on the portal client — the two cookies are independent. */
  exitStaff: () => api.post<void>("/admin/impersonation/exit"),
  /** Ends the live PORTAL impersonation from the ADMIN side, so a start that
   *  was refused for being a second concurrent one can proceed. Distinct from
   *  the portal client's own `exit`: this rides the STAFF cookie and is callable
   *  from this tab, whereas `exit` rides the portal cookie and is only reachable
   *  from inside the impersonated tab — which is exactly the tab an admin who
   *  needs this has lost track of. */
  endPortal: () => api.post<void>("/admin/impersonation/portal/end"),
  ensureQaUser: (body: EnsureQaUserRequest) =>
    api.post<EnsureQaUserResultDto>("/admin/qa-users/ensure", body),
};

/**
 * The seven real portal ACCOUNT TYPES — the ones a QA account can be
 * provisioned for and the ones "Log in as" routes to.
 *
 * Deliberately module-local, and deliberately NOT derived from the staff
 * `MaterialAudience` union in `api/portalMaterials.ts`: that union is the
 * materials-library / collection-filing vocabulary, and the two only look
 * alike. It carries "All" (a filter value that provisions nothing) and
 * "JuniorBroker" (a filing category, not an account type) while OMITTING
 * "LeadGenerator", which is a real portal account type with a real route
 * (`/portal/lead-generator`) — so this list cannot be expressed in terms of
 * it in either direction. The portal's own `MaterialAudience` (portal/api/
 * types.ts) does include LeadGenerator, but portal isolation forbids a staff
 * module importing it.
 *
 * The shared `MATERIAL_AUDIENCES` array is a third, narrower thing again (it
 * drives the signup-wizard's provisioning picker); adding an entry to it that
 * the generic signup flow cannot provision is the documented footgun.
 */
export const IMPERSONATABLE_AUDIENCES = [
  "Broker",
  "Buyer",
  "Seller",
  "Vendor",
  "Tps",
  "LeadGenerator",
  "Brokerage",
] as const;

export type ImpersonatableAudience = (typeof IMPERSONATABLE_AUDIENCES)[number];

/**
 * Where a portal account of a given audience lands after impersonation starts.
 *
 * Mirrors `dashboardPathFor` in `portal/pages/RecipientDashboardPage.tsx` and
 * the routes in `portal/PortalApp.tsx` — it is a deliberate local copy, not an
 * oversight: that function is a PORTAL file and the isolation guard
 * (`portal/isolation.test.ts`) forbids a staff page importing it. Unknown /
 * absent audiences fall back to `/portal`, which routes to the redeem screen
 * rather than a dead URL.
 */
const PORTAL_PATH_BY_AUDIENCE: Record<ImpersonatableAudience, string> = {
  Broker: "/portal/broker",
  Buyer: "/portal/buyer",
  Seller: "/portal/seller",
  Vendor: "/portal/vendor",
  Tps: "/portal/tps",
  LeadGenerator: "/portal/lead-generator",
  Brokerage: "/portal/brokerage",
};

export function portalPathForAudience(accountType: string | null): string {
  if (!accountType) return "/portal";
  return (
    PORTAL_PATH_BY_AUDIENCE[accountType as ImpersonatableAudience] ?? "/portal"
  );
}
